import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import AdminLayout from '../../components/admin/AdminLayout';
import { GET_AGENCY_PACKAGES } from '../../graphql/admin/queries/package';
import { DELETE_PACKAGE } from '../../graphql/admin/mutations/package';
import type { Package } from '../../types/package';
import type { Client } from '../../types/client';
import type { PackageClient } from '../../types/package-client';
import PackageClientFormModal from '../../components/admin/PackageClientFormModal';
import { getImageUrl } from '../../utils/imageUrl';

// Fonction utilitaire pour forcer le formatage
function getValidDate(dateValue: any): Date | null {
  
  if (!dateValue) {
    return null;
  }
  
  try {
    // Si c'est un timestamp (nombre ou chaîne de nombre)
    if (typeof dateValue === 'number' || (typeof dateValue === 'string' && /^\d+$/.test(dateValue))) {
      const timestamp = typeof dateValue === 'string' ? parseInt(dateValue, 10) : dateValue;
      const d = new Date(timestamp);
      const isValid = !isNaN(d.getTime());
      return isValid ? d : null;
    }
    
    // Si c'est une chaîne ISO ou une date au format YYYY-MM-DD
    if (typeof dateValue === 'string') {
      
      // Si c'est une date au format YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
        const d = new Date(dateValue);
        const isValid = !isNaN(d.getTime());
        return isValid ? d : null;
      }
      
      // Si c'est une chaîne ISO
      const d = new Date(dateValue);
      const isValid = !isNaN(d.getTime());
      return isValid ? d : null;
    }
    
    // Si c'est déjà un objet Date
    if (dateValue instanceof Date) {
      const isValid = !isNaN(dateValue.getTime());
      return isValid ? dateValue : null;
    }
    
    return null;
  } catch (error) {
    console.error('getValidDate - Erreur lors du parsing:', error);
    return null;
  }
}

// Fonction pour formater la date en français
function formatDate(date: Date | null): string {
  
  if (!date) {
    return 'Date invalide';
  }
  
  try {
    const formatted = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    return formatted;
  } catch (error) {
    console.error('formatDate - Erreur lors du formatage:', error);
    return 'Date invalide';
  }
}

export default function PackagesPage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedPackageClient, setSelectedPackageClient] = useState<PackageClient | null>(null);

  const { loading, error, data, refetch } = useQuery(GET_AGENCY_PACKAGES, {
    variables: { agencyName: user.agencyName },
    skip: !user.agencyName,
  });

  const [deletePackage] = useMutation(DELETE_PACKAGE, {
    onCompleted: () => {
      refetch(); // Rafraîchir la liste après suppression
    },
    onError: (error) => {
      alert(`Erreur lors de la suppression : ${error.message}`);
    },
  });

  const handleDelete = async (packageId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce package ? Cette action est irréversible.')) {
      try {
        await deletePackage({
          variables: { id: packageId },
        });
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div>Chargement...</div>
      </AdminLayout>
    );
  }

  if (error || !user.agencyName) {
    return (
      <AdminLayout>
        <div className="text-red-600">
          {error ? error.message : 'Aucune agence associée à cet utilisateur'}
        </div>
      </AdminLayout>
    );
  }

  const packages = data?.agencyPackages || [];

  const filteredPackages = packages.filter(pkg => 
    selectedType === 'all' || pkg.type === selectedType
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Packages</h1>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="all">Tous</option>
              <option value="B2B">B2B</option>
              <option value="B2C">B2C</option>
            </select>
          </div>
          <Link
            to="/admin/packages/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouveau Package
          </Link>
        </div>

        {/* Vue mobile : cartes */}
        <div className="md:hidden space-y-4">
          {filteredPackages.map((pkg: Package) => (
            <div key={pkg.id} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                <img
                  src={getImageUrl(pkg.image)}
                  alt={pkg.title}
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900 truncate">{pkg.title}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {pkg.category}
                    </span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      pkg.type === 'B2B' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {pkg.type}
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 text-sm text-gray-600 space-y-1">
                <div>
                  <span className="font-medium text-gray-700">Groupe :</span>{' '}
                  {pkg.groupSize ? `${pkg.groupSize} personnes` : 'Non spécifié'}
                </div>
                {pkg.departures && pkg.departures.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Départs :</span>{' '}
                    {[...pkg.departures]
                      .sort((a, b) => (getValidDate(a.departureDate)?.getTime() || 0) - (getValidDate(b.departureDate)?.getTime() || 0))
                      .slice(0, 2)
                      .map((d) => formatDate(getValidDate(d.departureDate)))
                      .join(', ')}
                    {pkg.departures.length > 2 && ` +${pkg.departures.length - 2}`}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-100">
                <Link
                  to={`/admin/packages/${pkg.id}`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-900"
                >
                  Modifier
                </Link>
                <button
                  onClick={() => navigate(`/admin/packages/${pkg.id}/clients`)}
                  className="inline-flex items-center text-sm font-medium text-green-600 hover:text-green-900"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Clients
                </button>
                <button
                  onClick={() => handleDelete(pkg.id)}
                  className="text-sm font-medium text-red-600 hover:text-red-900"
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
          {filteredPackages.length === 0 && (
            <div className="text-center text-gray-500 py-8">Aucun package trouvé</div>
          )}
        </div>

        {/* Vue desktop : tableau */}
        <div className="hidden md:block bg-white shadow rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taille du groupe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prochains départs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPackages.map((pkg: Package) => (
                  <tr key={pkg.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          src={getImageUrl(pkg.image)}
                          alt={pkg.title}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{pkg.title}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {pkg.groupSize ? `${pkg.groupSize} personnes` : 'Non spécifié'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {pkg.departures && pkg.departures.length > 0 ? (
                        <div className="space-y-1">
                          {[...pkg.departures]
                            .sort((a, b) => {
                              const dateA = getValidDate(a.departureDate);
                              const dateB = getValidDate(b.departureDate);
                              return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
                            })
                            .slice(0, 2)
                            .map((departure, index) => {
                              const date = getValidDate(departure.departureDate);
                              return (
                                <div key={departure.id} className="text-xs">
                                  {formatDate(date)}
                                </div>
                              );
                            })}
                          {pkg.departures.length > 2 && (
                            <div className="text-xs text-gray-400">
                              +{pkg.departures.length - 2} autres dates
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Aucune date</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {pkg.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        pkg.type === 'B2B' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {pkg.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4">
                      <Link
                        to={`/admin/packages/${pkg.id}`}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Modifier
                      </Link>
                      <button 
                        onClick={() => handleDelete(pkg.id)}
                        className="text-red-600 hover:text-red-900 mr-4"
                      >
                        Supprimer
                      </button>
                      <button
                        onClick={() => navigate(`/admin/packages/${pkg.id}/clients`)}
                        className="inline-flex items-center text-green-600 hover:text-green-900"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Gérer les clients
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <PackageClientFormModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedClient(null);
          }}
          packageId={selectedPackageClient?.packageId || 0}
          client={selectedClient}
          packageClient={selectedPackageClient}
        />
      </div>
    </AdminLayout>
  );
}